import { PageRequest } from '@/core/request/PageRequest';
import { Manufacturer } from '../../operational/Manufacturers/Manufacturers.types';
import { StockItemType } from '../StockItems/StockItems.types';

export enum QuotationStatus {
  Configuring,
  AwaitingOffers,
  Answered,
  WinnersSelected,
  Done,
}

export const QuotationStatusLabel = {
  [QuotationStatus.Configuring]: 'domain.enums.quotation_status.configuring',
  [QuotationStatus.AwaitingOffers]:
    'domain.enums.quotation_status.awaiting_offers',
  [QuotationStatus.WinnersSelected]:
    'domain.enums.quotation_status.winners_selected',
  [QuotationStatus.Answered]: 'domain.enums.quotation_status.answered',
  [QuotationStatus.Done]: 'domain.enums.quotation_status.done',
};

export const QuotationStatusColor = {
  [QuotationStatus.Configuring]: 'blue',
  [QuotationStatus.AwaitingOffers]: 'yellow',
  [QuotationStatus.WinnersSelected]: 'violet',
  [QuotationStatus.Answered]: 'teal',
  [QuotationStatus.Done]: 'green',
};

export enum QuotationSupplierStatus {
  NotOffered,
  Offered,
}

export const QuotationSuppliersLabel = {
  [QuotationSupplierStatus.NotOffered]:
    'domain.enums.quotation_supplier_status.not_offered',
  [QuotationSupplierStatus.Offered]:
    'domain.enums.quotation_supplier_status.offered',
};

export interface suppliersQuotation {
  id: number;
  name: string;
  winner: boolean;
  answered: boolean;
  quote: boolean;
}

export interface Quotation {
  id: number;
  company: {
    id: number;
    name: string;
  };
  employee: {
    id: number;
    name: string;
  };
  suppliers: suppliersQuotation[];
  date: string;
  status: QuotationStatus;
  answered: boolean;
  quote: boolean;
  winner: boolean;
}

export interface QuotationsQuery extends PageRequest {
  search?: string;
  date?: string;
  workOrderNumber?: string;
  licensePlate?: string;
  status?: QuotationStatus;
}

export interface QuotationGetResponse {
  result: Quotation;
}

export interface CreateQuotationRequest {
  companyId: number;
  employeeId: number;
  date: string;
}

export interface UpdateQuotationRequest {
  id: number;
  companyId: number;
  employeeId: number;
  date: string;
}

export interface Attachments {
  content: string;
  fileName: string;
}

export interface SendQuotationRequest {
  suppliers: {
    id: number;
    email: string;
  }[];
  subject: string;
  content: string;
  attachments: Attachments[];
}

export interface QuotationItem {
  id: number;
  workOrderNumber: string;
  manufacturingCode: string;
  name: string;
  manufacturer: Manufacturer;
  quantity: number;
  partId: number;
  workItemId: number;
  consumableId: number;
  type: StockItemType;
}

export interface CreateQuotationItemRequest {
  quotationId: number;
  items: {
    manufacturerId: number;
    partId: number;
    consumableId: number;
    quantity: number;
    workItemId: number;
  }[];
}

export interface UpdateQuotationItemRequest {
  quotationId: number;
  id: number;
  quantity: number;
  manufacturerId: number;
}

export interface QuotationSupplier {
  code: string;
  quotationId: number;
  supplierId: number;
  name: string;
  email: string;
  type: string;
  status: QuotationSupplierStatus;
}

export interface CreateQuotationSupplierRequest {
  quotationId: number;
  supplierIds: number[];
}

export interface ReportQuotationItemsByQuotationItem {
  code: string;
  description: string;
  requestedQuantity: number;
}

export interface ReportQuotationCompany {
  name?: string;
  logo?: string | null;
}

export interface ReportQuotationItemsByQuotation {
  quotationId: number;
  company: ReportQuotationCompany;
  items: ReportQuotationItemsByQuotationItem[];
}

export interface ReportQuotationItemsByQuotationResponse {
  result: ReportQuotationItemsByQuotation;
}

export interface ReportQuotationItem {
  code: string;
  description: string;
  requestedQuantity: number;
  suppliedQuantity: number;
  unitPrice: number;
  discount: number;
  total: number;
}

export interface ReportQuotationSupplierWinnerQuotation {
  supplier: {
    id: number;
    name: string;
  };
  items: ReportQuotationItem[];
}

export interface ReportQuotationSupplierWinners {
  quotationId: number;
  company: ReportQuotationCompany;
  quotations: ReportQuotationSupplierWinnerQuotation[];
}

export interface ReportQuotationSupplierWinnersResponse {
  result: ReportQuotationSupplierWinners;
}

export interface ReportQuotationItemsSupplier {
  name: string;
  items: [
    {
      code: string;
      itemOfferId: number;
      description: string;
      requestedQuantity: number;
      suppliedQuantity: number;
      unitPrice: number;
      discount: number;
      total: number;
    },
  ];
}

export interface ReportQuotationItemsSuppliers {
  company: ReportQuotationCompany;
  quotation: ReportQuotationItemsSupplier[];
}

export interface ReportQuotationItemsSuppliersResponse {
  result: ReportQuotationItemsSuppliers;
}

export enum ReportQuotationType {
  ItemsSupplier,
  SupplierItems,
}

export interface ReportQuotationItemsSuppliersRequest {
  quotationId?: number;
  type?: ReportQuotationType;
}

export enum QuotationType {
  ItemsSuppliers = '0',
  SupplierItems = '1',
  QuotationItems = '2',
  SupplierWinners = '3',
}
